class TakuRewrapConfigurator {
  constructor(root) {
    this.root = root;
    this.formId = root.dataset.formId;
    this.packIndex = Number(root.dataset.packIndex);
    this.ready = root.dataset.ready === 'true';
    this.designMode = root.dataset.designMode === 'true';
    this.data = JSON.parse(root.querySelector('[data-config-data]').textContent);
    this.ui = this.data.ui || {};
    this.tierButtons = [...root.querySelectorAll('[data-config-tier]')];
    this.slides = [...root.querySelectorAll('[data-config-slide]')];
    this.components = this.data.components;
    this.selectedParentVariantId = Number(this.data.selectedParentVariantId);
    this.showDesignName = root.dataset.showDesignName !== 'false';
    this.showDesignAvailability = root.dataset.showDesignAvailability !== 'false';
    this.activeSlot = 0;
    this.slots = [];
    this.lastSelection = null;
    this.loading = false;
    this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'es-ES', { style: 'currency', currency: this.data.currency, minimumFractionDigits: 2 });
    this.bindGallery();
    if (this.ready) { this.bind(); this.applyDefaultPack(); if (this.resetFromVariant()) this.render(); }
  }

  get form() { return document.getElementById(this.formId); }
  money(cents) { return this.formatter.format((Number(cents) || 0) / 100); }
  template(value, variables = {}) { return Object.entries(variables).reduce((text, [key, replacement]) => text.replaceAll(`[${key}]`, replacement), String(value || '')); }
  packCount(value) {
    const normalized = String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
    const match = normalized.match(/(?:^|\D)(3|5|8)(?:\D|$)/);
    return match ? Number(match[1]) : null;
  }

  parentVariant() {
    const formId = Number(this.form?.querySelector('[name="id"]')?.value);
    const selectedId = this.selectedParentVariantId || formId;
    return this.data.parentVariants.find((variant) => variant.id === selectedId) || null;
  }

  packCountFromVariant(variant) {
    if (!variant) return null;
    const indexed = this.packCount(variant.options?.[this.packIndex]);
    if (indexed) return indexed;
    const tierValues = new Set(this.tierButtons.map((button) => button.dataset.optionValue));
    const realOption = variant.options?.find((option) => tierValues.has(option));
    return this.packCount(realOption);
  }

  resetFromVariant() {
    const count = this.packCountFromVariant(this.parentVariant());
    if (!count) { this.blockInvalidCount(); return false; }
    this.unblockInvalidCount();
    this.resizeSlots(count);
    return true;
  }

  resizeSlots(count) {
    const previous = this.slots;
    this.slots = Array.from({ length: count }, (_, index) => previous[index] || null);
    const firstEmpty = this.slots.findIndex((slot) => !slot);
    this.activeSlot = firstEmpty >= 0 ? firstEmpty : Math.min(this.activeSlot, count - 1);
  }

  blockInvalidCount() {
    this.invalidCount = true;
    this.slots = [];
    const ui = this.root.querySelector('[data-config-purchase-ui]');
    if (ui) ui.hidden = true;
    const warning = this.root.querySelector('[data-config-count-warning]');
    if (warning) warning.hidden = !this.designMode;
  }

  unblockInvalidCount() {
    this.invalidCount = false;
    const ui = this.root.querySelector('[data-config-purchase-ui]');
    if (ui) ui.hidden = false;
    const warning = this.root.querySelector('[data-config-count-warning]');
    if (warning) warning.hidden = true;
  }

  applyDefaultPack() {
    const preferred = this.packCount(this.root.dataset.defaultPack);
    if (!preferred) return;
    const variant = this.data.parentVariants.find((candidate) => this.packCountFromVariant(candidate) === preferred && candidate.available);
    if (variant && this.selectParentVariant(variant)) this.selectedParentVariantId = variant.id;
  }

  bindGallery() {
    const track = this.root.querySelector('[data-config-track]');
    [...this.root.querySelectorAll('[data-config-thumb]')].forEach((button) => button.addEventListener('click', () => this.showSlide(Number(button.dataset.configThumb), true)));
    let frame;
    track?.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => { if (window.innerWidth < 990) this.galleryState(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))); }); }, { passive: true });
  }

  showSlide(index, smooth = false) {
    const track = this.root.querySelector('[data-config-track]');
    if (!this.slides[index]) return;
    if (window.innerWidth < 990) track.scrollTo({ left: index * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    this.galleryState(index);
  }

  galleryState(index) {
    this.slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === index); if (i === index) slide.removeAttribute('inert'); else slide.setAttribute('inert', ''); });
    this.root.querySelectorAll('[data-config-thumb]').forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    const counter = this.root.querySelector('[data-config-counter]'); if (counter) counter.textContent = `${index + 1} / ${this.slides.length || 1}`;
  }

  bind() {
    this.tierButtons.forEach((button) => button.addEventListener('click', () => this.selectTier(button)));
    this.root.querySelectorAll('[data-config-close]').forEach((button) => button.addEventListener('click', () => this.closePicker()));
    this.root.querySelector('[data-config-apply-all]')?.addEventListener('click', () => this.applyAll());
    this.root.querySelector('[data-config-repeat]')?.addEventListener('click', () => this.repeatPrevious());
    this.root.querySelector('[data-config-prev]')?.addEventListener('click', () => this.setActiveSlot(this.activeSlot - 1));
    this.root.querySelector('[data-config-next]')?.addEventListener('click', () => this.setActiveSlot(this.activeSlot + 1));
    this.root.querySelector('[data-config-submit]').addEventListener('click', () => this.addToCart());
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closePicker(); });
    document.addEventListener('variant:change', () => window.setTimeout(() => this.syncParent(), 80));
  }

  selectParentVariant(variant) {
    const input = this.form?.querySelector('[name="id"]');
    if (!input || !variant) return false;
    input.value = String(variant.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  selectTier(button) {
    const candidate = this.data.parentVariants.find((variant) => variant.options?.[this.packIndex] === button.dataset.optionValue);
    const count = this.packCountFromVariant(candidate);
    if (!count || !this.selectParentVariant(candidate)) { this.blockInvalidCount(); return; }
    this.selectedParentVariantId = candidate.id;
    this.unblockInvalidCount();
    this.resizeSlots(count);
    this.render();
    window.setTimeout(() => this.syncParent(), 80);
  }

  syncParent() {
    const formVariantId = Number(this.form?.querySelector('[name="id"]')?.value);
    if (this.data.parentVariants.some((variant) => variant.id === formVariantId)) this.selectedParentVariantId = formVariantId;
    const variant = this.parentVariant();
    const count = this.packCountFromVariant(variant);
    if (!count) { this.blockInvalidCount(); return; }
    this.unblockInvalidCount();
    if (count !== this.slots.length) this.resizeSlots(count);
    if (variant?.featuredMediaId) { const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === variant.featuredMediaId); if (index >= 0) this.showSlide(index, true); }
    this.render();
  }

  inventoryLimit(component) {
    if (!component.available) return 0;
    if (component.inventoryManagement !== 'shopify' || component.inventoryPolicy === 'continue') return Infinity;
    return Math.max(0, component.inventoryQuantity || 0);
  }

  selectedCount(id, excludingIndex = -1) { return this.slots.filter((component, index) => index !== excludingIndex && component?.id === id).length; }
  canSelect(component, index) { return this.selectedCount(component.id, index) < this.inventoryLimit(component); }

  openPicker(index, trigger) {
    this.activeSlot = index; this.pickerTrigger = trigger;
    this.set('[data-config-picker-title]', `${this.ui.packLabel || ''} ${index + 1}`.trim());
    this.renderDesigns();
    const picker = this.root.querySelector('[data-config-picker]'); picker.hidden = false; document.body.style.overflow = 'hidden';
    picker.querySelector('.taku-config__design:not(:disabled), [data-config-close]')?.focus();
  }

  closePicker() { const picker = this.root.querySelector('[data-config-picker]'); if (!picker || picker.hidden) return; picker.hidden = true; document.body.style.overflow = ''; this.pickerTrigger?.focus(); }

  choose(component) {
    if (!this.canSelect(component, this.activeSlot)) return;
    this.slots[this.activeSlot] = component; this.lastSelection = component;
    const next = this.slots.findIndex((slot) => !slot); this.activeSlot = next >= 0 ? next : this.activeSlot;
    this.closePicker(); this.render();
    window.setTimeout(() => this.root.querySelector(`[data-config-slot="${this.activeSlot}"] button`)?.focus(), 0);
  }

  applyAll() {
    if (!this.lastSelection || this.slots.length > this.inventoryLimit(this.lastSelection)) return;
    this.slots = this.slots.map(() => this.lastSelection); this.render();
  }

  repeatPrevious() {
    if (this.activeSlot === 0) return;
    const component = this.slots[this.activeSlot - 1] || this.lastSelection;
    if (!component || !this.canSelect(component, this.activeSlot)) return;
    this.slots[this.activeSlot] = component; this.lastSelection = component;
    const next = this.slots.findIndex((slot) => !slot); if (next >= 0) this.activeSlot = next;
    this.render();
  }

  renderDesigns() {
    const target = this.root.querySelector('[data-config-designs]');
    if (!target) return;
    target.replaceChildren(...this.components.map((component) => {
      const button = document.createElement('button'); const chosen = this.selectedCount(component.id); const available = this.canSelect(component, this.activeSlot); const selected = this.slots[this.activeSlot]?.id === component.id;
      button.type = 'button'; button.className = `taku-config__design${selected ? ' is-selected' : ''}`; button.disabled = !available; button.setAttribute('aria-pressed', String(selected)); button.setAttribute('aria-label', component.name);
      const stateText = selected ? this.ui.selectedText : available ? this.ui.availableText : this.ui.soldOutText;
      button.innerHTML = `<span>${component.image ? `<img src="${this.escape(component.image)}" alt="" width="124" height="124" loading="lazy">` : `<b aria-hidden="true">${this.escape(component.name.slice(0,1))}</b>`}<i aria-hidden="true">✓</i></span><span>${this.showDesignName ? `<strong>${this.escape(component.name)}</strong>` : ''}${this.showDesignAvailability ? `<small>${this.escape(stateText)}</small>` : ''}</span><em>${chosen ? this.escape(this.template(this.ui.chosenCount, { quantity: chosen })) : ''}</em>`;
      button.addEventListener('click', () => this.choose(component)); return button;
    }));
  }

  renderSlots() {
    const target = this.root.querySelector('[data-config-slots]');
    if (!target) return;
    target.replaceChildren(...this.slots.map((component, index) => {
      const card = document.createElement('article'); card.className = `taku-config__slot${index === this.activeSlot ? ' is-active' : ''}${index === this.activeSlot && !component ? ' is-current' : ''}`; card.dataset.configSlot = index;
      card.innerHTML = `<span class="taku-config__slot-media">${component?.image ? `<img src="${this.escape(component.image)}" alt="" width="104" height="104" loading="lazy">` : index + 1}</span><span class="taku-config__slot-copy"><strong>${this.escape(this.ui.packLabel)} ${index + 1}</strong><span>${component ? this.escape(component.name) : this.escape(this.ui.emptyPackText)}</span></span><button type="button">${this.escape(component ? this.ui.changeText : this.ui.chooseText)}</button>`;
      card.querySelector('button').addEventListener('click', (event) => this.openPicker(index, event.currentTarget)); return card;
    }));
  }

  grouped() {
    const chosen = this.slots.filter(Boolean); return [...new Set(chosen.map((item) => item.id))].map((id) => { const component = chosen.find((item) => item.id === id); return { component, quantity: chosen.filter((item) => item.id === id).length }; });
  }

  setActiveSlot(index) {
    if (!this.slots.length) return;
    this.activeSlot = Math.max(0, Math.min(index, this.slots.length - 1));
    this.render();
  }

  renderNavigation() {
    const indicators = this.root.querySelector('[data-config-indicators]');
    if (!indicators) return;
    indicators.replaceChildren(...this.slots.map((component, index) => {
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = index + 1; button.className = index === this.activeSlot ? 'is-active' : '';
      if (component) button.classList.add('is-complete');
      button.setAttribute('aria-label', `${this.ui.packLabel} ${index + 1}${component ? ` ${this.ui.configuredText}` : ''}`.trim());
      button.addEventListener('click', () => this.setActiveSlot(index));
      return button;
    }));
    const previous = this.root.querySelector('[data-config-prev]'); if (previous) previous.disabled = this.activeSlot === 0;
    const next = this.root.querySelector('[data-config-next]'); if (next) next.disabled = this.activeSlot === this.slots.length - 1;
  }

  render() {
    const parent = this.parentVariant(); const count = this.packCountFromVariant(parent);
    if (!count) { this.blockInvalidCount(); return; }
    this.unblockInvalidCount();
    if (count !== this.slots.length) this.resizeSlots(count);
    const complete = this.slots.every(Boolean);
    this.tierButtons.forEach((button) => { const candidate = this.data.parentVariants.find((variant) => variant.options[this.packIndex] === button.dataset.optionValue); button.disabled = !candidate?.available; button.setAttribute('aria-pressed', String(candidate?.id === parent?.id)); button.querySelector('[data-tier-price]').textContent = candidate ? this.money(candidate.price) : 'No disponible'; });
    this.renderSlots(); this.renderDesigns(); this.renderNavigation();
    const selected = this.slots.filter(Boolean).length; const variables = { current: selected, total: count, wraps: count * 3 };
    this.set('[data-config-progress]', this.template(this.ui.progressText, variables)); const progressBar = this.root.querySelector('[data-config-progress-bar]'); if (progressBar) progressBar.style.width = `${count ? selected / count * 100 : 0}%`;
    this.set('[data-config-builder-title]', this.template(this.ui.customizationTitle, variables));
    const applyAll = this.root.querySelector('[data-config-apply-all]'); if (applyAll) { applyAll.hidden = count <= 1; applyAll.disabled = !this.lastSelection || count > this.inventoryLimit(this.lastSelection); }
    const repeat = this.root.querySelector('[data-config-repeat]'); if (repeat) { repeat.hidden = this.activeSlot === 0; repeat.disabled = complete || !(this.slots[this.activeSlot - 1] || this.lastSelection); }
    const groups = this.grouped(); const list = this.root.querySelector('[data-summary-designs]'); if (list) list.replaceChildren(...(groups.length ? groups.map(({ component, quantity }) => this.template(this.ui.summaryItem, { quantity, name: component.name })) : [this.ui.incompleteText]).map((text) => { const li = document.createElement('li'); li.textContent = text; return li; }));
    this.set('[data-summary-tier]', complete ? this.template(this.ui.summaryComplete, variables) : this.template(this.ui.progressText, variables)); this.set('[data-summary-packs]', count); this.set('[data-summary-wraps]', count * 3); this.set('[data-summary-per-pack]', parent ? this.money(parent.price / count) : '—'); this.set('[data-summary-per-wrap]', parent ? this.money(parent.price / (count * 3)) : '—'); this.set('[data-summary-total]', parent ? this.money(parent.price) : '—'); this.set('[data-summary-stock]', complete && parent?.available ? this.ui.availableText : complete ? this.ui.soldOutText : this.ui.incompleteText);
    const savings = Math.max(0, Number(parent?.compareAtPrice || 0) - Number(parent?.price || 0)); this.set('[data-summary-savings]', savings ? this.money(savings) : '—'); const savingsRow = this.root.querySelector('[data-summary-savings-row]'); if (savingsRow) savingsRow.hidden = !savings;
    const submit = this.root.querySelector('[data-config-submit]'); submit.disabled = !complete || !parent?.available || this.loading; submit.textContent = this.loading ? this.ui.ctaLoading : complete ? this.ui.ctaComplete : this.template(this.ui.ctaIncomplete, variables);
  }

  validateStock() { return this.grouped().every(({ component, quantity }) => quantity <= this.inventoryLimit(component)); }

  async addToCart() {
    if (this.loading || this.slots.some((slot) => !slot) || !this.validateStock()) { this.error(this.ui.stockError); return; }
    const parent = this.parentVariant(); if (!parent?.available) { this.error(this.ui.parentError); return; }
    this.loading = true; this.error(''); this.render();
    const bundleId = `rewrap-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    const packImageProperties = Object.fromEntries(this.slots.map((component, index) => [`_rewrap_pack_image_${index + 1}`, component.image || '']));
    const items = [{ id: parent.id, quantity: 1, properties: { _rewrap_bundle_id: bundleId, Configuración: `${this.slots.length} packs · ${this.slots.length * 3} ReWrap`, ...packImageProperties } }, ...this.slots.map((component, index) => ({ id: component.id, quantity: 1, parent_id: parent.id, properties: { _rewrap_bundle_id: bundleId, Pack: String(index + 1), 'Diseño': component.name } }))];
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification'); const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: window.location.pathname }) });
      const result = await response.json(); if (!response.ok || result.status) throw new Error(result.description || result.message || this.ui.cartError);
      if (cart) { cart.classList.remove('is-empty'); cart.renderContents(result); cart.open?.(); } else window.location.href = `${window.Shopify.routes.root}cart`;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rewrap-configurator', cartData: result });
    } catch (error) { this.error(error.message); } finally { this.loading = false; this.render(); }
  }

  set(selector, value) { const node = this.root.querySelector(selector); if (node) node.textContent = value; }
  error(message) { const node = this.root.querySelector('[data-config-error]'); node.hidden = !message; node.textContent = message; }
  escape(value) { const node = document.createElement('div'); node.textContent = String(value || ''); return node.innerHTML; }
}

const initializeTakuConfigurator = (scope = document) => scope.querySelectorAll('[data-taku-config]').forEach((root) => { if (root.dataset.initialized !== 'true') { root.dataset.initialized = 'true'; new TakuRewrapConfigurator(root); } });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeTakuConfigurator()); else initializeTakuConfigurator();
document.addEventListener('shopify:section:load', (event) => initializeTakuConfigurator(event.target));
